import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ChecklistPreviewItem from './subcomponents/ChecklistPreviewItem';
import { useTypedDispatch, useTypedSelector } from '../../state/hooks';
import { ChecklistFormType, QuizQuestionStatus, ChecklistType, LayoutOrientations, SimulationStates, QuizType } from '../../globals';
import { StoreActions } from '../../state/store';
import Scenario from '../../classes/scenarioElements/Scenario';
import Panel from '../layout/Panel';
import Spacer from '../layout/Spacer';
import Button from '../layout/Button';
import Separator from '../layout/Separator';
import QuizManager from '../../classes/QuizManager';

export default function ChecklistPreviewContainer(props)
{
	const dispatch = useTypedDispatch();
	const currentStep = Scenario.currentStep();

	const stepChecklist = useTypedSelector(state => state.checklistModule.checklist[props.step]);
	const isQuiz = useMemo(() =>
	{
		return stepChecklist?.type === ChecklistFormType.QUIZ;
	}, [stepChecklist]);
	
	const stepChecklistItems = useTypedSelector(state => !stepChecklist ? null : isQuiz ? stepChecklist?.questions : stepChecklist?.items);
	const stepChecklistAnswers = useTypedSelector(state => !stepChecklist ? null : isQuiz ? state.checklistModule.answers[props.step] : null);
	const stepChecklistAttempts = useTypedSelector(state => state.checklistModule.attempts[props.step]);
	const quizSettings = useTypedSelector(state => state.checklistModule.quizSettings);
	const simulationState = useTypedSelector(state => state.applicationModule.simulationState);
	const nextStepOriginal = useTypedSelector(state => state.checklistModule.nextStep);
	const questionStatus = useTypedSelector(state => state.checklistModule.questionStatus);

	const [tempFormAnswers, setTempFormAnswers] = useState({});

	const isGraded = useMemo(() =>
	{
		return quizSettings.type === QuizType.GRADED && stepChecklist?.questions[0]?.score && stepChecklist?.questions[0]?.score > 0;
	}, [quizSettings, stepChecklist]);

	const getAttempts = useMemo(() =>
	{
		return stepChecklistAttempts ? stepChecklistAttempts >= 0 ? stepChecklistAttempts : "Unlimited" : 0;
	}, [stepChecklistAttempts]);

	const mustRespondFulfilled = useMemo(() =>
	{
		if (!stepChecklistItems) 
		{
			return true;
		}

		for (const item of stepChecklistItems)
		{
			if (item.type === ChecklistType.CHECKBOX) continue; //no point in forcing must respond on checkbox
			if (item.mustRespond === true)
			{
				if (isQuiz)
				{
					if (!stepChecklistAnswers) 
					{
						return false;
					}

					for (const ansewr of stepChecklistAnswers)
					{
						if (ansewr === true) 
						{
							return true;
						}
					}

					return false;
				}
				else
				{
					for (let i = 0; i < stepChecklistItems.length; i++)
					{
						if (stepChecklistItems[i].mustRespond && !tempFormAnswers[i])
						{
							return false;
						}
					}
				}
			}
		}
		return true;
	}, [stepChecklistItems, isQuiz, stepChecklistAnswers, tempFormAnswers]);

	const nextStep = useMemo(() =>
	{
		let nextStep = Scenario.getStep(stepChecklist?.next);
		if(nextStep)
		{
			return nextStep;
		}
		else
		{
			nextStep = Scenario.getStep(nextStepOriginal);
			if(nextStep && nextStep.id !== currentStep?.id)
			{
				return nextStep;
			}
		}

		return undefined;
	}, [stepChecklist, currentStep, nextStepOriginal]);

	useEffect(() =>
	{
		dispatch(StoreActions.checklist.setQuestionStatus(QuizManager.getAnswerStatus(props.step)));
		if (stepChecklistAttempts === undefined && isQuiz && stepChecklistItems && stepChecklistItems.length > 0 && currentStep)
		{
			dispatch(StoreActions.checklist.setRemainingAttempts({ step: currentStep.id, attempts: stepChecklistItems[0].attempts }));
			setTempFormAnswers({});
		}
	}, [stepChecklist, isQuiz, stepChecklistItems, stepChecklistAttempts, currentStep]);

	const checklistPreviewItems = useMemo(() =>
	{
		const items = new Array<React.ReactNode>();
		if (!stepChecklistItems) 
		{
			return items;
		}
		
		for (let i = 0; i < stepChecklistItems.length; i++)
		{
			items.push(
				<ChecklistPreviewItem
					{...props}
					index={i}
					id={stepChecklistItems[i].id}
					key={stepChecklistItems[i].id}
					item={stepChecklistItems[i]}
					isQuiz={isQuiz}
					pointerEvents={simulationState === SimulationStates.Playing ? "all" : "none"}
					questionStatus={questionStatus}
					attempts={getAttempts}
					quizSettings={quizSettings}
					answers={stepChecklistAnswers}
					onChangeCallback={(index, v) =>
					{
						if (isQuiz)
						{
							QuizManager.setAnswer(index, v);
						}
						else
						{
							const tempData = { ...tempFormAnswers }
							tempData[i] = v;
							setTempFormAnswers(tempData);
						}
					}}
				/>
			);
		}
		return items;
	}, [stepChecklistItems, stepChecklistAnswers, stepChecklistAttempts, quizSettings, simulationState, questionStatus, tempFormAnswers, props.step]);

	const checkAnswers = useCallback(() =>
	{
		if (!stepChecklistItems) return false;

		const question = stepChecklistItems[0];
		const answers = stepChecklistAnswers ? stepChecklistAnswers : new Array(stepChecklistItems.at(0)?.options?.length || 1).fill(null);

		if (stepChecklistAttempts === 0)
		{
			// console.log('out of attempts')
			return false;
		}
		else if (stepChecklistAttempts > 0 && currentStep)
		{
			dispatch(StoreActions.checklist.setRemainingAttempts({ step: currentStep.id, attempts: (stepChecklistAttempts - 1) }));
		}

		if (question.options && question.options.length > 0)
		{
			for (let i = 0; i < question.options.length; i++)
			{
				const answer = answers[i];
				if ((answer !== true && question.options[i].correct === true) ||
					(answer === true && question.options[i].correct !== true)
				)
				{
					// console.log('wrong answer');
					return false;
				}
			}
		}

		// console.log("correct answer");
		return true;
	}, [stepChecklistItems, stepChecklistAnswers, stepChecklistAttempts, dispatch]);

	const validateAnswers = useCallback(() =>
	{
		const answerStatus = checkAnswers() ? QuizQuestionStatus.CORRECT : QuizQuestionStatus.INCORRECT;

		dispatch(StoreActions.checklist.setQuestionStatus(answerStatus));
		QuizManager.onAnswerStatusChanged(answerStatus);
	}, [checkAnswers, dispatch]);

	const closeChecklist = useCallback(() =>
	{
		if (simulationState === SimulationStates.Playing)
		{
			if (isQuiz)
			{
				if (questionStatus === QuizQuestionStatus.UNANSWERED || (questionStatus === QuizQuestionStatus.INCORRECT && getAttempts !== 0))
				{
					return;
				}
			}

			dispatch(StoreActions.checklist.setPreviewProperties({ simulationEvent: "" }));
			if (props.callback)
			{
				props.callback();
			}

			if (nextStep && currentStep)
			{
				dispatch(StoreActions.scenario.addStepToHistory(currentStep.id));
				Scenario.setCurrentStep(nextStep, true);
				return;
			}
		}

		dispatch(StoreActions.checklist.setPreviewProperties({ next: "" }));
	}, [simulationState, isQuiz, questionStatus, dispatch, props.callback, nextStepOriginal]);	

	function generateFooterLabel()
	{
		switch (questionStatus)
		{
			case QuizQuestionStatus.UNANSWERED:
				if (isQuiz && getAttempts !== 'Unlimited')
				{
					return <span className={'quizSimulationFooter'} style={{ color: '#C4C4C4' }}>{`${getAttempts} attempts remaining`}</span>
				}
				break;
			case QuizQuestionStatus.CORRECT:
				return <span className={'quizSimulationFooter'} style={{ color: '#26BB66', width: '100%' }}>{`Correct!`}</span>
			case QuizQuestionStatus.INCORRECT:
				if (stepChecklistAttempts === 0 && stepChecklistItems?.length && stepChecklistAnswers?.length)
				{
					const question = stepChecklistItems[0];
					for (let i = 0; i < question.options.length; i++)
					{
						const answer = stepChecklistAnswers.at(i) || false;
						if (answer === true && question.options[i].correct !== true)
						{
							return <span className={'quizSimulationFooter'} style={{ color: '#EB5757', width: '100%' }}>{`Incorrect`}</span>
						}
					}
					return <span className={'quizSimulationFooter'} style={{ color: '#EB5757', width: '100%' }}>{`Incorrect. Not all correct options selected`}</span>
				}
				return <span className={'quizSimulationFooter'} style={{ color: '#EB5757', width: '100%' }}>{`Incorrect`}</span>
		}
	}

	return (
		<Panel className={'uiSidebarPreviewContainer'}>
			<Panel className={'checklistItemsHolder'}>
				{
					isQuiz &&
						<>
							<Spacer size={{ y: '10px' }} />
							<span style={{ marginLeft: '14px', color: 'gray' }}>{isGraded ? "Graded Assessment" : "Knowledge Check"}</span>
						</>
				}
				<Panel //this panel contains the list items
					expand={true}
					className={simulationState === SimulationStates.Playing ? 'allowScrollY' : 'allowScrollY'}
					style={{ overflowX: 'hidden' }}>
					{checklistPreviewItems}
				</Panel>
				<Panel
					expand={false}
					className={'bottomContent centerContentHorizontally'}
					style={{ height: '120px' }}
				>
					<Separator orientation={LayoutOrientations.Horizontal} className={"solid"} />
					<Spacer size={{ y: '20px' }} />
					{
						generateFooterLabel()
					}
					<Spacer size={{ y: '10px' }} />
					{
						!isQuiz ?
							<Button
								label={nextStep ? 'Next Step' : 'Close'}
								className={simulationState === SimulationStates.Playing ? 'checklistContinueButtonSim' : 'checklistContinueButton'}
								disabled={!((mustRespondFulfilled || !nextStep) && simulationState === SimulationStates.Playing)}
								style={{
									backgroundColor: nextStep ? '#DFA330' : '#333333',
									outline: nextStep ? '' : 'solid 1px #757575',
									outlineOffset: nextStep ? '' : '-2px'
								}}
								onClick={closeChecklist}
							/> 
							:
							<>
								{
									questionStatus === QuizQuestionStatus.UNANSWERED ?
										<Button
											label={"Submit"}
											className={simulationState === SimulationStates.Playing ? 'checklistContinueButtonSim' : 'checklistContinueButton'}
											disabled={!(mustRespondFulfilled && simulationState === SimulationStates.Playing)}
											style={{ backgroundColor: '#DFA330' }}
											onClick={validateAnswers}
										/>
										: 
										questionStatus === QuizQuestionStatus.CORRECT ?
											<Button
												label={"Continue"}
												className={simulationState === SimulationStates.Playing ? 'checklistContinueButtonSim' : 'checklistContinueButton'}
												disabled={!(mustRespondFulfilled && simulationState === SimulationStates.Playing)}
												style={{ backgroundColor: '#26BB66' }}
												onClick={closeChecklist}
											/> 
											:
											<Button
												label={getAttempts !== 0 ? "Try Again" : "Continue"}
												className={simulationState === SimulationStates.Playing ? 'checklistContinueButtonSim' : 'checklistContinueButton'}
												disabled={!(mustRespondFulfilled && simulationState === SimulationStates.Playing)}
												style={{ backgroundColor: '#D84C4C' }}
												onClick=
													{
														() =>
														{
															if (getAttempts !== 0)
															{
																QuizManager.resetAnswers();
																QuizManager.onAnswerStatusChanged(QuizQuestionStatus.UNANSWERED);
															}
															else
															{
																closeChecklist();
															}
														}
													}
											/>
								}
							</>
					}
				</Panel>
			</Panel>
		</Panel>
	)
}
